import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getArticleById, listArticles, searchArticles } from "../../../../services/contentServices";
import { formatArticleDate } from "./articleMagazineFormatters";
import { contentEditorCardSx, contentCardContentSx, contentFieldSx, contentSectionTitleSx } from "../contentEditorStyles";

const RECENT_ARTICLES_LIMIT = 10;
const SEARCH_MIN_LENGTH = 2;
const MAX_LINKED_ARTICLES = 3;

const mergeArticleOptions = (items, selectedIds, currentArticleId, previousOptions = []) => {
  const map = new Map();
  selectedIds.forEach((id) => {
    const existing = previousOptions.find((option) => option.id === id)
      || items.find((option) => option.id === id);
    map.set(id, existing || { id, title: id });
  });
  items.forEach((item) => {
    if (item.id !== currentArticleId) {
      map.set(item.id, item);
    }
  });
  return [...map.values()];
};

const normalizeSummary = (article) => ({
  id: String(article.id),
  title: article.title || "",
  subtitle: article.subtitle || "",
  image: article.image || "",
  category: article.category || "",
  publishedAt: article.publishedAt,
});

const LinkedArticleOptionRow = ({ option, selected }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 1,
      width: "100%",
      py: 0.25,
    }}
  >
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3 }}>
        {option.title || option.id}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mt: 0.25 }}>
        {[option.category, formatArticleDate(option.publishedAt)].filter(Boolean).join(" · ") || "—"}
      </Typography>
    </Box>
    {selected ? (
      <CheckCircleIcon sx={{ color: "#008245", fontSize: 20, mt: 0.25, flexShrink: 0 }} aria-hidden="true" />
    ) : null}
  </Box>
);

LinkedArticleOptionRow.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    category: PropTypes.string,
    publishedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  selected: PropTypes.bool.isRequired,
};

const LinkedArticleSelectedCard = ({ article, index, total, onMoveUp, onMoveDown }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.25,
      p: 1,
      borderRadius: 1.5,
      border: "1px solid #e8edf3",
      backgroundColor: "#ffffff",
      alignItems: "stretch",
    }}
  >
    <Box
      sx={{
        width: 88,
        minWidth: 88,
        height: 66,
        borderRadius: 1,
        border: "1px solid #e8edf3",
        backgroundColor: "#f1f5f9",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {article.image ? (
        <Box
          component="img"
          src={article.image}
          alt=""
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : null}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 0.25 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.3 }} noWrap>
        {article.title || article.id}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }} noWrap>
        {[article.category, formatArticleDate(article.publishedAt)].filter(Boolean).join(" · ") || "—"}
      </Typography>
    </Box>
    <Stack direction="column" spacing={0.25} justifyContent="center">
      <IconButton size="small" disabled={index === 0} onClick={onMoveUp} aria-label="Sposta articolo collegato su">
        <ArrowUpwardIcon fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        disabled={index >= total - 1}
        onClick={onMoveDown}
        aria-label="Sposta articolo collegato giu"
      >
        <ArrowDownwardIcon fontSize="small" />
      </IconButton>
    </Stack>
  </Box>
);

LinkedArticleSelectedCard.propTypes = {
  article: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    image: PropTypes.string,
    category: PropTypes.string,
    publishedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  onMoveUp: PropTypes.func.isRequired,
  onMoveDown: PropTypes.func.isRequired,
};

const isPlaceholderOption = (option) => !option?.title || option.title === option.id;

const LinkedArticlesSelect = ({ selectedIds, currentArticleId, onChange, onError }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const selectedOptions = useMemo(
    () => selectedIds.map((id) => options.find((option) => option.id === id) || { id, title: id }),
    [options, selectedIds]
  );

  const loadRecentArticles = useCallback(async () => {
    try {
      const recent = (await listArticles())
        .filter((item) => item.id !== currentArticleId)
        .slice(0, RECENT_ARTICLES_LIMIT);
      setOptions((previous) => mergeArticleOptions(recent, selectedIds, currentArticleId, previous));
    } catch (error) {
      onError(error.message);
    }
  }, [currentArticleId, onError, selectedIds]);

  useEffect(() => {
    loadRecentArticles();
  }, [loadRecentArticles]);

  useEffect(() => {
    const query = inputValue.trim();
    if (query.length < SEARCH_MIN_LENGTH) {
      return undefined;
    }
    const timeout = setTimeout(async () => {
      try {
        const results = await searchArticles({ search: query, excludeId: currentArticleId, limit: 10 });
        setOptions((previous) => mergeArticleOptions(results, selectedIds, currentArticleId, previous));
      } catch (error) {
        onError(error.message);
      }
    }, 250);
    return () => clearTimeout(timeout);
  }, [currentArticleId, inputValue, onError, selectedIds]);

  useEffect(() => {
    let cancelled = false;

    const loadMissingSelected = async () => {
      const idsToLoad = selectedIds.filter((id) =>
        isPlaceholderOption(optionsRef.current.find((option) => option.id === id))
      );
      if (!idsToLoad.length) {
        return;
      }
      try {
        const loaded = await Promise.all(
          idsToLoad.map(async (id) => {
            try {
              return normalizeSummary(await getArticleById(id));
            } catch {
              return { id, title: id, category: "", image: "", publishedAt: null };
            }
          })
        );
        if (!cancelled) {
          setOptions((previous) => mergeArticleOptions(loaded, selectedIds, currentArticleId, previous));
        }
      } catch (error) {
        if (!cancelled) {
          onError(error.message);
        }
      }
    };

    loadMissingSelected();
    return () => {
      cancelled = true;
    };
  }, [currentArticleId, onError, selectedIds]);

  const moveSelected = (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= selectedIds.length) {
      return;
    }
    const next = [...selectedIds];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(next);
  };

  return (
    <Card sx={contentEditorCardSx}>
      <CardContent sx={contentCardContentSx}>
        <Typography sx={contentSectionTitleSx}>Articoli collegati</Typography>
        <Autocomplete
          multiple
          disableCloseOnSelect
          filterSelectedOptions={false}
          value={selectedOptions}
          options={options}
          inputValue={inputValue}
          onInputChange={(_, value, reason) => {
            setInputValue(value);
            if (reason === "clear" || value.trim().length < SEARCH_MIN_LENGTH) {
              loadRecentArticles();
            }
          }}
          onChange={(_, values) => onChange(values.slice(0, MAX_LINKED_ARTICLES).map((item) => item.id))}
          getOptionLabel={(option) => option.title || option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="Nessun articolo trovato"
          renderTags={() => null}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={{
                  "&.MuiAutocomplete-option": {
                    alignItems: "flex-start",
                  },
                  "&.MuiAutocomplete-option[aria-selected='true']": {
                    backgroundColor: "transparent !important",
                  },
                  "&.MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
                    backgroundColor: "rgba(11, 61, 135, 0.08) !important",
                  },
                }}
              >
                <LinkedArticleOptionRow option={option} selected={selected} />
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cerca articoli"
              sx={contentFieldSx}
            />
          )}
          slotProps={{
            paper: {
              sx: {
                "& .MuiAutocomplete-option[aria-selected='true']": {
                  backgroundColor: "transparent",
                },
              },
            },
          }}
        />

        {selectedOptions.length ? (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#64748b" }}>
              Articoli selezionati ({selectedOptions.length}/{MAX_LINKED_ARTICLES})
            </Typography>
            {selectedOptions.map((article, index) => (
              <LinkedArticleSelectedCard
                key={article.id}
                article={article}
                index={index}
                total={selectedOptions.length}
                onMoveUp={() => moveSelected(index, "up")}
                onMoveDown={() => moveSelected(index, "down")}
              />
            ))}
          </Stack>
        ) : null}
      </CardContent>
    </Card>
  );
};

LinkedArticlesSelect.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  currentArticleId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default LinkedArticlesSelect;
